<template>
  <div class="email-chip-input-wrap">
    <div
      class="email-chip-input"
      :class="{
        'is-focused': isFocused,
        'has-error': Boolean(errorMessage),
      }"
      @click="focusInput"
    >
      <div
        v-if="emails.length"
        ref="container"
        class="email-chip-input__chips"
        :class="{ 'is-collapsed': isCollapsed }"
      >
        <span
          v-for="(email, index) in visibleEmails"
          :key="`${email}-${index}`"
          class="email-chip"
          :title="email"
        >
          <span class="email-chip__label">{{ email }}</span>
          <button
            type="button"
            class="email-chip__remove"
            :aria-label="`Remove ${email}`"
            @mousedown.prevent
            @click.stop="removeEmail(index)"
          >
            <fluent-icon icon="dismiss" size="12" />
          </button>
        </span>
        <span
          v-if="isCollapsed && overflowCount > 0"
          class="email-chip email-chip--more"
          :title="hiddenEmails.join(', ')"
          @click.stop="expandAndFocus"
        >
          +{{ overflowCount }}
        </span>
      </div>
      <input
        ref="input"
        v-model="draft"
        type="text"
        class="email-chip-input__field"
        :class="{
          'is-second-line': emails.length > 0 && isFocused,
          'is-input-hidden': emails.length > 0 && !isFocused,
        }"
        :placeholder="emails.length && !isFocused ? '' : placeholder"
        @keydown="onKeydown"
        @input="onDraftInput"
        @blur="onBlur"
        @focus="onFocus"
        @paste="onPaste"
      />
    </div>
    <!-- Off-screen row used only to measure chip widths for +n overflow -->
    <div ref="measure" class="email-chip-input__measure" aria-hidden="true">
      <span
        v-for="(email, index) in emails"
        :key="`measure-${email}-${index}`"
        class="email-chip"
      >
        <span class="email-chip__label">{{ email }}</span>
        <button type="button" class="email-chip__remove" tabindex="-1">
          <fluent-icon icon="dismiss" size="12" />
        </button>
      </span>
      <span ref="moreMeasure" class="email-chip email-chip--more">+99</span>
    </div>
    <span v-if="errorMessage" class="email-chip-input__error">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script>
import { isValidEmail, parseEmailList } from './helpers/emailHeadHelper';

export default {
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    errorText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      emails: [],
      draft: '',
      isFocused: false,
      isExpanded: false,
      errorMessage: '',
      maxVisible: Infinity,
      resizeObserver: null,
      // Skip blur→parent sync when recipients are applied from reply-all
      suppressBlurEmit: false,
    };
  },
  computed: {
    // Keep a single chip row with +n unless the user expands via the +n chip
    isCollapsed() {
      return !this.isExpanded;
    },
    visibleEmails() {
      if (!this.isCollapsed) return this.emails;
      if (!Number.isFinite(this.maxVisible)) return this.emails;
      return this.emails.slice(0, this.maxVisible);
    },
    overflowCount() {
      if (!this.isCollapsed || !Number.isFinite(this.maxVisible)) return 0;
      return Math.max(0, this.emails.length - this.maxVisible);
    },
    hiddenEmails() {
      if (!this.overflowCount) return [];
      return this.emails.slice(this.maxVisible);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        const parsed = parseEmailList(newVal);
        const valid = [];
        const invalid = [];
        parsed.forEach(email => {
          if (isValidEmail(email)) {
            valid.push(email);
          } else {
            invalid.push(email);
          }
        });

        const current = this.emails.join(', ');
        const incoming = valid.join(', ');
        if (current !== incoming) {
          // External recipient updates (reply-all) must not emit blur/sync
          // that can overwrite sibling To/CC/Bcc fields with stale empties.
          if (this.isFocused && valid.length > 0) {
            this.suppressBlurEmit = true;
            this.isFocused = false;
            this.isExpanded = false;
          }
          this.emails = valid;
        }

        if (invalid.length) {
          this.errorMessage = this.validationError();
          if (!this.isFocused || !this.draft.trim()) {
            this.draft = invalid.join(', ');
          }
        } else if (!this.draft.trim()) {
          this.errorMessage = '';
        }
      },
    },
    emails: {
      handler() {
        this.scheduleRecalculate();
        this.$nextTick(() => this.observeChipsRow());
      },
      deep: true,
    },
    isExpanded(expanded) {
      if (expanded) {
        this.maxVisible = this.emails.length;
      } else {
        this.scheduleRecalculate();
      }
    },
  },
  mounted() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleRecalculate();
      });
    }
    this.observeChipsRow();
    this.scheduleRecalculate();
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect();
  },
  methods: {
    focusInput() {
      this.isFocused = true;
      this.$nextTick(() => {
        this.$refs.input?.focus();
      });
    },
    expandAndFocus() {
      this.isExpanded = true;
      this.maxVisible = this.emails.length;
      this.focusInput();
    },
    observeChipsRow() {
      if (!this.resizeObserver) return;
      this.resizeObserver.disconnect();
      if (this.$refs.container) {
        this.resizeObserver.observe(this.$refs.container);
      }
    },
    scheduleRecalculate() {
      this.$nextTick(() => this.recalculateVisible());
    },
    recalculateVisible() {
      if (!this.isCollapsed) {
        this.maxVisible = this.emails.length;
        return;
      }

      const container = this.$refs.container;
      const measure = this.$refs.measure;
      if (!container || !measure || !this.emails.length) {
        this.maxVisible = this.emails.length;
        return;
      }

      const chipEls = Array.from(measure.querySelectorAll('.email-chip')).filter(
        el => !el.classList.contains('email-chip--more')
      );
      const moreEl = this.$refs.moreMeasure;
      if (!chipEls.length) {
        this.maxVisible = this.emails.length;
        return;
      }

      const styles = window.getComputedStyle(container);
      const gap = parseFloat(styles.columnGap || styles.gap) || 6;
      const available = container.clientWidth;
      const moreWidth = moreEl ? moreEl.offsetWidth : 40;

      let used = 0;
      let count = 0;

      for (let i = 0; i < chipEls.length; i += 1) {
        const chipWidth = chipEls[i].offsetWidth;
        const nextUsed = used + (count > 0 ? gap : 0) + chipWidth;
        const remainingAfter = chipEls.length - (i + 1);
        const reserved = remainingAfter > 0 ? gap + moreWidth : 0;

        if (nextUsed + reserved > available) {
          break;
        }

        used = nextUsed;
        count += 1;
      }

      // Always keep at least one chip visible when space is extremely tight
      if (count === 0 && chipEls.length) {
        count = 1;
      }

      this.maxVisible = count;
    },
    validationError() {
      return (
        this.errorText ||
        this.$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR')
      );
    },
    emitChange() {
      this.$emit('input', this.emails.join(', '));
      this.$emit('change', this.emails.join(', '));
    },
    addEmails(rawValues) {
      const candidates = Array.isArray(rawValues)
        ? rawValues
        : parseEmailList(rawValues);

      const valid = [];
      const invalid = [];

      candidates.forEach(email => {
        const trimmed = email.trim();
        if (!trimmed) return;
        if (isValidEmail(trimmed)) {
          valid.push(trimmed);
        } else {
          invalid.push(trimmed);
        }
      });

      let added = false;
      valid.forEach(email => {
        const normalized = email.toLowerCase();
        if (this.emails.some(existing => existing.toLowerCase() === normalized)) {
          return;
        }
        this.emails.push(email);
        added = true;
      });

      if (added) {
        this.emitChange();
      }

      if (invalid.length) {
        this.errorMessage = this.validationError();
        return invalid;
      }

      this.errorMessage = '';
      return [];
    },
    commitDraft({ clearOnInvalid = false } = {}) {
      if (!this.draft.trim()) return true;

      const invalid = this.addEmails(this.draft);
      if (invalid.length) {
        // Keep invalid text in the input so the user can fix it; do not add a chip
        this.draft = clearOnInvalid ? '' : invalid.join(', ');
        return false;
      }

      this.draft = '';
      return true;
    },
    removeEmail(index) {
      // When collapsed, visible index maps 1:1 to emails index
      this.emails.splice(index, 1);
      this.errorMessage = '';
      this.emitChange();
      this.focusInput();
    },
    onFocus() {
      this.isFocused = true;
    },
    onKeydown(event) {
      if (['Enter', ',', ';'].includes(event.key)) {
        if (this.draft.trim()) {
          event.preventDefault();
          this.commitDraft();
        }
        return;
      }

      if (event.key === 'Tab' && this.draft.trim()) {
        // Only move focus if the draft commits as a valid chip
        if (!this.commitDraft()) {
          event.preventDefault();
        }
        return;
      }

      if (event.key === 'Backspace' && !this.draft && this.emails.length) {
        event.preventDefault();
        this.removeEmail(this.emails.length - 1);
      }
    },
    onDraftInput() {
      if (this.errorMessage) {
        this.errorMessage = '';
      }
      // Commit when user types a separator mid-string (e.g. paste-like typing)
      if (/[,;]\s*$/.test(this.draft)) {
        this.commitDraft();
      }
    },
    onPaste(event) {
      const text = event.clipboardData?.getData('text');
      if (!text) return;

      const parsed = parseEmailList(text);
      if (parsed.length <= 1 && !/[,;\s]/.test(text)) return;

      event.preventDefault();
      const invalid = this.addEmails(parsed);
      this.draft = invalid.length ? invalid.join(', ') : '';
    },
    onBlur() {
      this.isFocused = false;
      this.isExpanded = false;
      this.commitDraft();
      if (this.suppressBlurEmit) {
        this.suppressBlurEmit = false;
        return;
      }
      this.$emit('blur');
    },
    // Commit any typed text before send; returns false if draft is invalid.
    flush() {
      if (!this.draft.trim()) {
        this.errorMessage = '';
        return true;
      }
      return this.commitDraft();
    },
  },
};
</script>

<style lang="scss" scoped>
.email-chip-input-wrap {
  @apply w-full relative;
}

.email-chip-input {
  @apply flex flex-col w-full min-h-[2rem] py-1 cursor-text;

  &.has-error {
    @apply pb-0;
  }
}

.email-chip-input__chips {
  @apply flex flex-wrap items-center gap-1.5 w-full;

  &.is-collapsed {
    @apply flex-nowrap overflow-hidden;
  }
}

.email-chip {
  @apply inline-flex items-center max-w-full gap-1 rounded-full px-2 py-0.5
    text-xs font-medium bg-slate-75 text-slate-800
    dark:bg-slate-700 dark:text-slate-100 shrink-0;
}

.email-chip--more {
  @apply cursor-pointer select-none shrink-0
    hover:bg-slate-100 dark:hover:bg-slate-600;
}

.email-chip__label {
  @apply truncate max-w-[14rem];
}

.email-chip__remove {
  @apply inline-flex items-center justify-center rounded-full p-0.5
    text-slate-500 hover:bg-slate-200 hover:text-slate-800
    dark:text-slate-300 dark:hover:bg-slate-600 dark:hover:text-slate-50;
}

.email-chip-input__field {
  @apply w-full min-w-0 border-0 bg-transparent outline-none shadow-none
    text-sm text-slate-800 dark:text-slate-100 mb-0 p-0;

  &.is-second-line {
    @apply mt-1;
  }

  // Keep input mounted (avoid v-show blur races) but remove the second line
  // when chips exist and the field is not focused.
  &.is-input-hidden {
    @apply absolute w-px h-px p-0 m-0 overflow-hidden border-0;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }
}

.email-chip-input__measure {
  @apply absolute flex items-center gap-1.5 whitespace-nowrap pointer-events-none;
  left: -9999px;
  top: 0;
  visibility: hidden;
  // Avoid max-w-full collapsing chip widths in an out-of-flow measure row
  width: max-content;

  .email-chip {
    max-width: none;
  }
}

.email-chip-input__error {
  @apply block text-sm text-red-500 dark:text-red-500 mt-0.5;
}
</style>
